import { Box, Button, Flex, HStack, Skeleton, VStack } from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';

import { ResolvedLoanFragmentGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { Typography } from 'packages/common-frontend/components/Typography';
import {
  formatCurrencyAmount,
  numberToPercentageLabel,
} from 'packages/common-frontend/utils';
import { Bank } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useGetCreditFacilityMetricsQuery } from 'packages/transactions/requests/transactions.gql.generated';

export const FacilityDetails = ({
  loan,
  loanLoading,
  showExternalFacilityId = false,
}: {
  loan: ResolvedLoanFragmentGQLType | undefined;
  loanLoading: boolean;
  showExternalFacilityId?: boolean;
}) => {
  const {
    data: metricsDataRequest,
    loading: metricsLoading,
  } = useGetCreditFacilityMetricsQuery({
    skip: !loan?.facility,
    variables: {
      creditFacilityId: loan?.facility.creditFacilityId as string,
      postingDate: commonDate.currentDateStr(),
    },
    fetchPolicy: 'no-cache',
  });

  const metrics = metricsDataRequest?.metrics;
  const isAnyLoading = loanLoading || metricsLoading;

  return (
    <VStack direction="column" gap="1rem" align="start" w="100%">
      <Typography variant="section-title" trim>
        Facility Details
      </Typography>
      <Flex
        sx={{
          align: 'start',
          borderRadius: 'md',
          borderWidth: '1px',
          gap: '.5rem',
          p: '2rem',
          w: '100%',
        }}
        direction="column"
      >
        <HStack justifyContent="start" gap="1rem" wrap="wrap">
          <Box minW="20rem">
            <Typography fontWeight="medium">Name</Typography>
          </Box>
          {isAnyLoading || !loan?.facility ? (
            <Skeleton w="20rem" h="1.5rem" />
          ) : (
            <Flex flexWrap="wrap" maxWidth="50%">
              <Button
                variant="link"
                as={RouterLink}
                to={`/portfolio/summary?creditFacility=${loan.facility.creditFacilityId}`}
              >
                <Typography variant="base-muted" colorScheme="blue">
                  {loan.facility.creditFacilityName}
                  {showExternalFacilityId &&
                    !!loan?.facility.externalFacilityId &&
                    ` (${loan.facility.externalFacilityId})`}
                </Typography>
              </Button>
            </Flex>
          )}
        </HStack>
        <HStack justifyContent="start" gap="1rem" wrap="wrap">
          <Box minW="20rem">
            <Typography fontWeight="medium">Borrower</Typography>
          </Box>
          {isAnyLoading || !loan?.facility ? (
            <Skeleton w="20rem" h="1.5rem" />
          ) : (
            <Flex flexWrap="wrap" maxWidth="50%">
              <Typography variant="base-muted">
                {loan.parties.borrowerOrganization.name}
              </Typography>
            </Flex>
          )}
        </HStack>
        <HStack justifyContent="start" gap="1rem" wrap="wrap">
          <Box minW="20rem">
            <Typography fontWeight="medium">Commitment</Typography>
          </Box>
          {isAnyLoading || !metrics ? (
            <Skeleton w="20rem" h="1.5rem" />
          ) : (
            <Typography variant="base-muted">
              {metrics?.commitmentAmount
                ? formatCurrencyAmount(
                    metrics.commitmentAmount,
                    Bank.Currency.USD,
                  )
                : '-'}
            </Typography>
          )}
        </HStack>
        <HStack justifyContent="start" gap="1rem" wrap="wrap">
          <Box minW="20rem">
            <Typography fontWeight="medium">Current Total Principal</Typography>
          </Box>
          {isAnyLoading || !metrics ? (
            <Skeleton w="20rem" h="1.5rem" />
          ) : (
            <Typography variant="base-muted">
              {metrics
                ? `${formatCurrencyAmount(
                    metrics.outstandingPrincipalBalance,
                    Bank.Currency.USD,
                  )} (${numberToPercentageLabel(
                    metrics.utilization,
                  )} Utilization)`
                : '-'}
            </Typography>
          )}
        </HStack>
      </Flex>
    </VStack>
  );
};
